import React, { ReactNode } from "react";
import styled, { css } from "styled-components";

// import { flagEmptyModule } from "@formant/core/model/flagEmptyModule";
import { Uuid } from "@formant/core/model/Uuid";
import { primary } from "@formant/ui/component/common/Colors";
import { Typography } from "@formant/ui/component/common/Typography";
import * as Menu from "@formant/ui/component/menu";
import { PopupPortal, usePopup } from "@formant/ui/hooks/usePopupHooks";

import { track } from "../analytics";
import { appStore, observeStore } from "../stores";
import { Header } from "./common/Header";
import { Hotkey } from "./common/hotkey/Hotkey";
import { RestrictedComponent } from "./common/RestrictedComponent";
import { Scrollable } from "./common/Scrollable";
import { StandardButton } from "./common/StandardButton";

export interface IInspectorProps {
    open?: boolean;
    className?: string;
    children: ReactNode;
    title?: string;
    hotKeyExit?: boolean;
    onBack: () => void;
    showNewDesign?: boolean;
    deviceIds?: Uuid[];
}

export const Inspector: React.FC<IInspectorProps> = props => {
    const {
        open,
        hotKeyExit,
        className,
        children,
        title,
        onBack,
        deviceIds,
        showNewDesign = false,
    } = props;

    const onAddModule = () => {
        observeStore.addModuleForDevices = deviceIds;
    };

    const createEmptyLineChart = () => {
        observeStore.addModuleComponent = "line_chart";
        onCloseDropdown();
    };

    const createEmptyTable = () => {
        observeStore.addModuleComponent = "numberic_table";
        onCloseDropdown();
    };

    const {
        isOpen,
        // openDropdown: onOpenDropdown,
        closeDropdown: onCloseDropdown,
        setRefElement,
        setTargetElement,
        context: popperContext,
    } = usePopup<HTMLButtonElement & HTMLAnchorElement, HTMLDivElement>({
        placement: "bottom-start",
        modifiers: [{ name: "offset", options: { offset: [0, 10] } }],
    });

    return (
        <InspectorComponent
            open={open}
            className={className}
            data-testid={
                open
                    ? "opened-inspector-component"
                    : "closed-inspector-component"
            }
        >
            {hotKeyExit && !appStore.snackbar && !appStore.confirm && (
                <Hotkey
                    code="esc"
                    label={`exit ${title}`}
                    context={title}
                    onHotkey={onBack}
                />
            )}

            {!showNewDesign && (
                <Top>
                    <Typography type="h3" color="silver">
                        Edit view
                    </Typography>
                    <StandardButton
                        primary={true}
                        onClick={() => {
                            track("Customize: Done");
                            return onBack();
                        }}
                        data-testid="edit-view-inspector-done-button"
                    >
                        Done
                    </StandardButton>
                </Top>
            )}

            {showNewDesign && (
                <StyledNewTop>
                    <RestrictedComponent
                        requiredAccess="administrator"
                        resource="views"
                    >
                        <StyledButton
                            dark={true}
                            ref={setRefElement}
                            onClick={onAddModule}
                            data-testid="inspector-done"
                        >
                            + Module
                        </StyledButton>
                        <PopupPortal isOpen={isOpen} context={popperContext}>
                            <Menu.Container
                                ref={setTargetElement}
                                onItemClicked={onCloseDropdown}
                                style={{ maxHeight: "calc(100vh - 75px)" }}
                            >
                                <Menu.Item
                                    data-testid="line-chart"
                                    onClick={createEmptyLineChart}
                                >
                                    Line chart
                                </Menu.Item>
                                <Menu.Item onClick={createEmptyTable}>
                                    Table
                                </Menu.Item>
                            </Menu.Container>
                        </PopupPortal>
                    </RestrictedComponent>
                    <StandardButton
                        primary={true}
                        onClick={() => {
                            track("Customize: Done");
                            return onBack();
                        }}
                        data-testid="edit-view-inspector-done-button"
                    >
                        Done
                    </StandardButton>
                </StyledNewTop>
            )}

            <Scroll dark={false}>
                {!showNewDesign && title && (
                    <Header
                        title={title}
                        hideMenu={true}
                        onBack={undefined}
                        setDocumentTitle={false}
                    />
                )}
                {children}
            </Scroll>
        </InspectorComponent>
    );
};

const Top = styled.div`
    display: flex;
    flex-direction: row;
    margin: 1rem;
    align-items: center;
    justify-content: space-between;
    margin-left: 2rem;
    margin-right: 2rem;
    max-height: 3rem;
    height: 3rem;
`;

const StyledNewTop = styled.div`
    display: flex;
    flex-direction: row;
    margin: 0;
    align-items: center;
    justify-content: flex-end;
    padding-right: 2rem;
    gap: 1rem;
    height: 4rem;
    max-height: 4rem;
    background: var(--formant-color-primary-steel01);
`;

const StyledButton = styled(StandardButton)`
    width: 7.8125rem;
`;

const InspectorComponent = styled.div<{ open?: boolean }>`
    ${props =>
        props.open
            ? css`
                  width: 20rem;
              `
            : css`
                  width: 0rem;
              `}
    overflow: hidden;
    background-color: ${primary.module};
    transition: width 0.3s linear;
    z-index: 100;
`;

const Scroll = styled(Scrollable)`
    height: 90vh;
    .control-bar-wrap {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
    }
    h1 {
        padding-left: 0.93rem;
    }
`;
